import { z } from 'zod';
import { booleanish, cents, dateKey, optionalText, paginationQuery, requiredText, hexColor } from './common';
import { PAYMENT_METHOD_TYPES, PAYMENT_PROVIDERS } from '../settings';
import { DRE_GROUPS } from '../finance/dre';
import { RECURRENCE_FREQUENCIES } from '../finance/recurrence';
import { CASHFLOW_HORIZONS, ENTRY_DATE_FIELDS, ENTRY_ORIGINS, ENTRY_STATUS_FILTERS, FINANCE_ACCOUNT_TYPES, STATEMENT_LINE_STATUSES } from '../finance/constants';

/** Mês "AAAA-MM". */
export const monthKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mês inválido (use AAAA-MM)');

/** Data e hora ISO ou só o dia "AAAA-MM-DD" (meio-dia no fuso da loja). */
export const instantOrDate = z.union([z.string().datetime({ offset: true }), dateKey]);

export const financeEntryInputSchema = z.object({
  type: z.enum(['income', 'expense']),
  description: requiredText(160, 'Descreva o lançamento'),
  amountCents: cents.refine((v) => v > 0, 'Informe o valor'),
  categoryId: z.string().uuid().nullish(),
  dueDate: dateKey,
  paid: z.boolean().default(false),
  paidAt: instantOrDate.nullish(),
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES).nullish(),
  counterparty: optionalText(120),
  customerId: z.string().uuid().nullish(),
  notes: optionalText(1000),
  /** Repetir mensalmente por N meses (1 = não repete). */
  repeatMonths: z.number().int().min(1).max(60).default(1),
  // ---- Financeiro v2
  /** Conta (caixa, banco, carteira, cartão). Pago sem conta: usa a conta da forma de pagamento ou a conta padrão. */
  accountId: z.string().uuid().nullish(),
  /** Mês a que o lançamento pertence no DRE (nulo = data de vencimento). */
  competenceDate: dateKey.nullish(),
  supplierId: z.string().uuid().nullish(),
  /** Número do boleto, da nota ou do contrato (vazio = sem número). */
  documentNumber: z.string().trim().max(60, 'Use no máximo 60 caracteres').nullish(),
});
export type FinanceEntryInput = z.infer<typeof financeEntryInputSchema>;

export const financeEntryUpdateSchema = financeEntryInputSchema.omit({ repeatMonths: true }).partial();

export const financeListQuery = paginationQuery.extend({
  type: z.enum(['income', 'expense']).optional(),
  /** pending = todos em aberto; upcoming = a vencer (depois de hoje); due_today; overdue; paid; canceled. */
  status: z.enum(ENTRY_STATUS_FILTERS).optional(),
  /** uuid da categoria ou "none" (sem categoria). */
  categoryId: z.union([z.string().uuid(), z.literal('none')]).optional(),
  from: dateKey.optional(),
  to: dateKey.optional(),
  q: z.string().trim().max(100).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  // ---- Financeiro v2
  /** uuid da conta ou "none" (sem conta). */
  accountId: z.union([z.string().uuid(), z.literal('none')]).optional(),
  supplierId: z.string().uuid().optional(),
  recurrenceId: z.string().uuid().optional(),
  /** Linha do DRE da categoria (ex.: "deductions" = impostos). */
  dreGroup: z.enum(DRE_GROUPS).optional(),
  /** Qual data o período (from/to) recorta. Padrão: vencimento. */
  dateField: z.enum(ENTRY_DATE_FIELDS).default('due'),
  origin: z.enum(ENTRY_ORIGINS).optional(),
  /** true = só conciliados com o extrato; false = só não conciliados. */
  reconciled: booleanish.optional(),
  /** Transferências entre contas ficam fora da lista (e dos totais) por padrão. */
  includeTransfers: booleanish.default(false),
  sort: z.enum(['date_asc', 'date_desc', 'amount_desc', 'amount_asc']).optional(),
});
export type FinanceListQuery = z.infer<typeof financeListQuery>;

export const financeCategoryInputSchema = z.object({
  name: requiredText(60),
  type: z.enum(['income', 'expense']),
  color: hexColor.nullish(),
  /** Linha do DRE. null = fora do resultado. Omitido na criação: receita = "other", despesa = "administrative". */
  dreGroup: z.enum(DRE_GROUPS).nullish(),
});

export const payEntrySchema = z.object({
  paidAt: instantOrDate.optional(),
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES).nullish(),
  /** Conta de onde saiu ou onde entrou o dinheiro. Omitida: conta do lançamento, da forma de pagamento ou a padrão. */
  accountId: z.string().uuid().nullish(),
});

/** Ações em lote na lista de lançamentos (até 200 por vez). */
export const financeEntriesBatchSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'Selecione ao menos um lançamento').max(200, 'Selecione no máximo 200 lançamentos'),
  action: z.enum(['pay', 'unpay', 'delete', 'recategorize']),
  /** pay: data do pagamento (padrão: agora). */
  paidAt: instantOrDate.optional(),
  /** pay: conta (padrão: a de cada lançamento, a da forma de pagamento ou a padrão). */
  accountId: z.string().uuid().nullish(),
  /** recategorize: nova categoria (null = sem categoria). */
  categoryId: z.string().uuid().nullish(),
});
export type FinanceEntriesBatchInput = z.infer<typeof financeEntriesBatchSchema>;

// ---------------------------------------------------------------- Contas
const accountBase = {
  name: requiredText(60, 'Dê um nome para a conta'),
  type: z.enum(FINANCE_ACCOUNT_TYPES),
  institution: optionalText(60),
  color: hexColor.nullish(),
  /** Saldo na data de início (pode ser negativo: cartão de crédito com fatura em aberto). */
  openingBalanceCents: z.number().int().min(-1_000_000_000).max(1_000_000_000),
  /** Data do saldo inicial. Movimentos anteriores não mudam o saldo. */
  openingDate: dateKey,
  sortOrder: z.number().int().min(0).max(999),
};

export const financeAccountInputSchema = z.object({
  ...accountBase,
  type: accountBase.type.default('bank'),
  openingBalanceCents: accountBase.openingBalanceCents.default(0),
  openingDate: accountBase.openingDate.optional(),
  sortOrder: accountBase.sortOrder.optional(),
  isDefault: z.boolean().optional(),
});
export type FinanceAccountInput = z.infer<typeof financeAccountInputSchema>;

/** PATCH: só as chaves enviadas. `isDefault: true` torna esta a conta padrão; `isActive: false` arquiva. */
export const financeAccountUpdateSchema = z.object({
  name: accountBase.name.optional(),
  type: accountBase.type.optional(),
  institution: optionalText(60).optional(),
  color: hexColor.nullish(),
  openingBalanceCents: accountBase.openingBalanceCents.optional(),
  openingDate: accountBase.openingDate.optional(),
  sortOrder: accountBase.sortOrder.optional(),
  isDefault: z.literal(true).optional(),
  isActive: z.boolean().optional(),
});
export type FinanceAccountUpdateInput = z.infer<typeof financeAccountUpdateSchema>;

export const financeAccountListQuery = z.object({
  includeArchived: booleanish.default(false),
});

/** Quais formas de pagamento (ids de settings.payment.methods) e provedores online caem nesta conta. Lista completa. */
export const financeAccountRoutingSchema = z.object({
  methodIds: z.array(z.string().min(1).max(40)).max(30),
  providers: z.array(z.enum(PAYMENT_PROVIDERS)).max(2).default([]),
});

export const accountStatementQuery = z.object({
  from: dateKey.optional(),
  to: dateKey.optional(),
});

// ---------------------------------------------------------------- Transferências
export const transferInputSchema = z
  .object({
    fromAccountId: z.string().uuid('Escolha a conta de origem'),
    toAccountId: z.string().uuid('Escolha a conta de destino'),
    amountCents: cents.refine((v) => v > 0, 'Informe o valor'),
    date: dateKey,
    description: optionalText(160),
    notes: optionalText(1000),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, { message: 'Escolha contas diferentes.', path: ['toAccountId'] });
export type TransferInput = z.infer<typeof transferInputSchema>;

export const transferListQuery = paginationQuery.extend({
  accountId: z.string().uuid().optional(),
  from: dateKey.optional(),
  to: dateKey.optional(),
});

// ---------------------------------------------------------------- Despesas e receitas fixas
const recurrenceBase = z.object({
  type: z.enum(['income', 'expense']),
  description: requiredText(160, 'Descreva a despesa fixa'),
  amountCents: cents.refine((v) => v > 0, 'Informe o valor'),
  categoryId: z.string().uuid().nullish(),
  accountId: z.string().uuid().nullish(),
  supplierId: z.string().uuid().nullish(),
  counterparty: optionalText(120),
  frequency: z.enum(RECURRENCE_FREQUENCIES),
  /** Dia do vencimento (1 a 31) nas frequências por mês; 31 vira o último dia em meses curtos. */
  dayOfMonth: z.number().int().min(1).max(31).nullish(),
  startDate: dateKey,
  endDate: dateKey.nullish(),
  isActive: z.boolean(),
  notes: optionalText(1000),
});

export const recurrenceInputSchema = recurrenceBase
  .extend({
    type: recurrenceBase.shape.type.default('expense'),
    frequency: recurrenceBase.shape.frequency.default('monthly'),
    isActive: recurrenceBase.shape.isActive.default(true),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, { message: 'A data final não pode ser antes do início.', path: ['endDate'] });
export type RecurrenceInput = z.infer<typeof recurrenceInputSchema>;

/** PATCH: só as chaves enviadas. `applyToPending` (padrão true) leva a mudança às contas ainda não pagas a partir de hoje. */
export const recurrenceUpdateSchema = recurrenceBase.partial().extend({
  applyToPending: z.boolean().optional(),
});
export type RecurrenceUpdateInput = z.infer<typeof recurrenceUpdateSchema>;

export const recurrenceDeleteQuery = z.object({
  /** true = mantém as contas futuras ainda não pagas geradas pela regra. */
  keepPending: booleanish.default(false),
});

// ---------------------------------------------------------------- Metas
export const goalMonthParam = z.object({ month: monthKey });

export const financeGoalInputSchema = z.object({
  revenueCents: cents.nullish(),
  profitCents: z.number().int().min(-1_000_000_000).max(1_000_000_000).nullish(),
  avgTicketCents: cents.nullish(),
  ordersCount: z.number().int().min(0).max(1_000_000).nullish(),
});
export type FinanceGoalInput = z.infer<typeof financeGoalInputSchema>;

// ---------------------------------------------------------------- Relatórios do financeiro
export const dreQuery = z.object({
  /** Mês "AAAA-MM" (padrão: mês atual). Alternativa: from/to (competência). */
  month: monthKey.optional(),
  from: dateKey.optional(),
  to: dateKey.optional(),
});

export const xrayQuery = z.object({ month: monthKey.optional() });

export const cashflowQuery = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((v) => (CASHFLOW_HORIZONS as readonly number[]).includes(v), 'Escolha 30, 60 ou 90 dias')
    .default(30),
});

// ---------------------------------------------------------------- Conciliação bancária
/** Envio do extrato em JSON (o arquivo também pode ir como multipart, campo "file"). */
export const statementUploadSchema = z.object({
  fileName: requiredText(200, 'Informe o nome do arquivo'),
  /** Conteúdo do arquivo em base64 (até 2 MB). */
  contentBase64: z.string().min(4, 'Envie o arquivo do extrato').max(2_800_000, 'Arquivo muito grande (máximo 2 MB)'),
  /** Força a inversão dos sinais (cartão que exporta compra como positivo). */
  invertSigns: booleanish.optional(),
});

export const statementLinesQuery = paginationQuery.extend({
  status: z.enum(STATEMENT_LINE_STATUSES).optional(),
  importId: z.string().uuid().optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/** Casa a linha com um lançamento (entryId) OU com um recebimento de venda/fiado (movementRef das sugestões). */
export const statementMatchSchema = z
  .object({
    entryId: z.string().uuid().optional(),
    movementRef: z.string().regex(/^(sale:[0-9a-f-]{36}:\d{1,2}|fiado:[0-9a-f-]{36})$/, 'Referência inválida').optional(),
  })
  .refine((v) => Boolean(v.entryId) !== Boolean(v.movementRef), { message: 'Escolha um lançamento ou um recebimento para conciliar.' });

/** Cria um lançamento (já pago, na conta do extrato) a partir da linha. */
export const statementCreateEntrySchema = z.object({
  categoryId: z.string().uuid().nullish(),
  description: optionalText(160),
  supplierId: z.string().uuid().nullish(),
  competenceDate: dateKey.nullish(),
  notes: optionalText(1000),
});

// ---------------------------------------------------------------- Caixa
export const openCashSchema = z.object({
  openingAmountCents: cents,
  notes: optionalText(300),
});

export const cashMovementSchema = z.object({
  type: z.enum(['withdrawal', 'deposit']),
  amountCents: cents.refine((v) => v > 0, 'Informe o valor'),
  reason: optionalText(200),
});

export const closeCashSchema = z.object({
  countedCashCents: cents,
  notes: optionalText(500),
});
